import UserRepository from "@/api/repositories/usersRepository";
import RoleRepository from "@/api/repositories/roleRepository";

import i18n from "@/plugins/i18n";

export const users = {
  namespaced: true,
  state: {
    token: undefined,
    users: [],
    permissions: [],
    user: {},
    currentUser: {},
    userCompany: {},
    credentials: { email: "", password: "" },
    hasCompany: false,
    updateCounter: 0,
    isSSO: false,
    status: {
      loading: false,
      loggedIn: false,
      loggingIn: false,
    },
  },

  actions: {
    refreshUserData({ commit }) {
      commit("UPDATE");
    },

    async getUser({ commit }) {
      commit("LOADING", true);

      return await UserRepository.get().then(
        (users) => {
          commit("GET_USER", users.data);
          commit("LOADING", false);
          return { sc: 200, data: users.data };
        },
        (res) => {
          commit("LOADING", false);
          return { sc: res.response.status, data: res.response.data };
        }
      );
    },

    async getById({ commit, dispatch, rootState }, userId) {
      commit("LOADING", true);

      var user = rootState.users.currentUser;

      await UserRepository.getById(user, userId)
        .then((users) => {
          commit("GET_USER", users.data);
          commit("LOADING", false);
        })
        .catch((res) => {
          if (res.response.status === 401) {
            dispatch(
              "users/login",
              {
                email: rootState.users.credentials.email,
                password: rootState.users.credentials.password,
              },
              { root: true }
            );
          }

          commit("LOADING", false);
        });
      commit("LOADING", false);
    },

    async getAll({ dispatch, commit, rootState }) {
      commit("LOADING", true);

      var user = rootState.users.currentUser;

      await UserRepository.getAll(user).then(
        (users) => {
          commit("GET_USERS", users.data);
          commit("LOADING", false);
        },
        (res) => {
          if (res.response.status === 401) {
            dispatch(
              "users/login",
              {
                email: rootState.users.credentials.email,
                password: rootState.users.credentials.password,
              },
              { root: true }
            );
          }

          commit("LOADING", false);
        }
      );
      commit("LOADING", false);
    },

    async getUserCompany({ dispatch, commit, rootState }) {
      commit("LOADING", true);
      var user = rootState.users.currentUser;

      await UserRepository.getCompany(user).then(
        (company) => {
          commit("GET_USER_COMPANY", company.data);
          commit("LOADING", false);
          if (company.data.country == "" || company.data.country == null)
            company.data.country = "en";

          dispatch("languages/setLanguage", company.data.country, {
            root: true,
          });
          dispatch("configuration/getCompanySettings", null, { root: true });
        },
        (res) => {
          if (res.response.status === 401) {
            dispatch(
              "users/login",
              {
                email: rootState.users.credentials.email,
                password: rootState.users.credentials.password,
              },
              { root: true }
            );
          }

          dispatch("alert/error", i18n.t("common.error"), { root: true });
          commit("LOADING", false);
        }
      );
    },

    setUserCompany({ dispatch, commit }, { company }) {
      commit("LOADING", true);

      if (company.country === "") company.country = null;

      commit("GET_USER_COMPANY", company);
      dispatch("languages/setLanguage", company.country ?? "en", {
        root: true,
      });

      dispatch("featureAccess/getCompanyFeatures", null, { root: true });

      commit("LOADING", false);
    },

    async setUserByPublicDashboard({ commit }, dashboard) {
      commit("LOGIN_USER", {
        token: dashboard.token,
        role: "PublicDashboard",
      });
      commit("SET_TOKEN", dashboard.token);
      commit("GET_USER_COMPANY", dashboard.dashboard.company);
    },

    async editUser({ dispatch, commit, rootState, state }, userPayload) {
      commit("LOADING", true);

      var user = rootState.users.currentUser;

      await UserRepository.edit(user, userPayload.userId, userPayload)
        .then((user) => {
          commit("EDIT_USER", user.data);
          commit("LOADING", false);

          if (state.currentUser.userId === user.data.userId) {
            dispatch("login", state.credentials);
          }
        })
        .catch((res) => {
          if (res.response.status === 401) {
            dispatch(
              "users/login",
              {
                email: rootState.users.credentials.email,
                password: rootState.users.credentials.password,
              },
              { root: true }
            );
          }

          dispatch("alert/error", i18n.t("user.updateFailed"), { root: true });
          commit("LOADING", false);
        });
    },

    async afterLoginActions({ dispatch }) {
      await dispatch("getUserCompany");
      await dispatch("companies/getAll", null, { root: true });
      await dispatch("getUserPermissionsByCurrentUser");
      await dispatch("configuration/getCompanySettings", null, {
        root: true,
      });
    },

    async login({ dispatch, rootState, commit }, { email, password }) {
      if (this.isSSO) return;

      if (
        email == undefined ||
        password == undefined ||
        email == "" ||
        password == ""
      ) {
        return false;
      }

      commit("SET_CREDENTIALS", { email: email, password: password });
      commit("SET_SSO", false);
      commit("SET_TOKEN", undefined);

      commit("LOADING", true);
      if (rootState.users.status.loggingIn) return;

      commit("LOGGING_IN", true);
      let result = await UserRepository.login(email, password)
        .then(async (user) => {
          commit("LOGIN_USER", user.data);
          commit("SET_TOKEN", user.data.token);
          await dispatch("users/afterLoginActions", null, { root: true });
          commit("LOADING", false);
          return true;
        })
        .catch((res) => {
          if (!res.response) {
            throw new Error(i18n.t("common.errors.backendConnection"));
          } else if (res.response.status == 409) {
            // The license is probably not valid
            throw new Error(res.response.data);
          }

          commit("LOGIN_FAILED");
          commit("LOADING", false);
          dispatch("alert/error", i18n.t("user.signInFailed"), { root: true });
          return false;
        });

      commit("LOGGING_IN", false);

      return result;
    },

    async getUserPermissionsByCurrentUser({ commit, rootState }) {
      await RoleRepository.getUserPermissionsByUserId(
        rootState.users.currentUser
      ).then((d) => commit("SET_PERMISSIONS", d.data));
    },

    async logout({ dispatch, commit }) {
      commit("LOGOUT_USER");
      dispatch("companies/resetState", null, { root: true });
      dispatch("tag/resetState", null, { root: true });
      dispatch("applications/resetState", null, { root: true });
      dispatch("tagData/resetState", null, { root: true });
      dispatch("alarms/resetState", null, { root: true });
      commit("LOADING", false);
    },

    async updateRole(
      { dispatch, commit, rootState },
      { role: role, targetUserId: targetUserId }
    ) {
      commit("LOADING", false);

      var user = rootState.users.currentUser;

      await UserRepository.updateRole(user, targetUserId, role)
        .then(() => {
          dispatch("companies/getCompanyUsers", null, { root: true });
        })
        .catch(() => {
          dispatch("alert/error", i18n.t("common.error"), { root: true });
        });
    },

    async createUserAndCompany({ dispatch, commit, rootState }, payload) {
      commit("LOADING", true);
      await UserRepository.createUserWithCompany(
        rootState.users.currentUser,
        payload
      )
        .then(() => {
          dispatch("alert/success", i18n.t("common.created"), {
            root: true,
          });
        })
        .catch((error) => {
          if (error.response.status === 409) {
            throw new Error(error.response.data);
          } else {
            dispatch("alert/error", i18n.t("common.error"), {
              root: true,
            });
          }
        });
    },

    async delete({ dispatch, commit, rootState }, userId) {
      commit("LOADING", true);

      var user = rootState.users.currentUser;
      if (user.userId !== userId) {
        await UserRepository.delete(user, userId)
          .then(() => {
            dispatch("alert/success", i18n.t("common.deleted"), {
              root: true,
            });
            commit("LOADING", false);
          })
          .catch((res) => {
            if (res.response.status === 401) {
              dispatch(
                "users/login",
                {
                  email: rootState.users.credentials.email,
                  password: rootState.users.credentials.password,
                },
                { root: true }
              );
            }

            dispatch("alert/error", i18n.t("common.error"), {
              root: true,
            });
          });
      }
    },

    async forgotPassword({ dispatch, commit }, email) {
      commit("LOADING", true);
      await UserRepository.forgotPassword(email)
        .then(() => {
          commit("LOADING", false);
        })
        .catch(() => {
          dispatch("alert/error", i18n.t("common.error"), {
            root: true,
          });
        });
      commit("LOADING", false);
    },

    async forgotPasswordUpdate({ dispatch, commit }, { guid, password }) {
      commit("LOADING", true);
      await UserRepository.forgotPasswordUpdate(guid, password)
        .then(() => {
          commit("LOADING", false);
        })
        .catch(() => {
          dispatch("alert/error", i18n.t("common.error"), {
            root: true,
          });
        });
      commit("LOADING", false);
    },
  },

  mutations: {
    LOADING(state, status) {
      state.status.loading = status;
    },

    GET_USER(state, user) {
      state.user = user;
    },

    GET_USERS(state, users) {
      state.users = users;
    },

    GET_USER_COMPANY(state, company) {
      state.userCompany = company;
      state.hasCompany = true;
    },

    SET_CREDENTIALS(state, credentials) {
      state.credentials = credentials;
    },

    EDIT_USER(state, user) {
      state.user = user;
    },

    LOGIN_USER(state, user) {
      state.currentUser = user;
      state.status.loggedIn = true;
    },

    LOGIN_FAILED(state) {
      state.status.loggedIn = false;
    },

    SET_PERMISSIONS(state, val) {
      state.permissions = val;
    },

    LOGGING_IN(state, val) {
      state.status.loggingIn = val;
    },

    // eslint-disable-next-line
    LOGOUT_USER(state) {
      state.users = [];
      state.user = [];
      state.currentUser = {};
      state.userCompany = {};
      state.credentials = { email: "", password: "" };
      state.hasCompany = false;
      state.permissions = [];
      state.status = {
        loading: false,
        loggedIn: false,
      };
    },

    UPDATE(state) {
      state.updateCounter += 1;
    },

    SET_TOKEN(state, token) {
      state.token = token;
    },

    SET_SSO(state, isSSO) {
      state.isSSO = isSSO;
    },
  },

  getters: {
    user(state) {
      return state.currentUser;
    },

    userId(state) {
      return state.currentUser?.userId ?? undefined;
    },

    companyId(state) {
      return state.userCompany.companyId;
    },

    companyName(state) {
      return state.userCompany.name;
    },

    token(state) {
      return state.token ?? undefined;
    },

    permissions(state) {
      return state.permissions;
    },

    isSSO(state) {
      return state.isSSO;
    },
  },
};
